import axios from "axios";
import { useEffect, useState } from "react"
import { useAuth, useMessage } from '../../global-components/src';
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./style.css"
import { errorMessage } from "../../global-components/src/utils/text";
import { BUCKET_PACOCA_URL, PACOCA_API_URL, PACOCA_URL } from "../../global-components/src/utils/vars";
import { User } from "../../types";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { Trash, Eye, EyeOff, BookOpen } from "lucide-react";
import Cookies from "js-cookie";
import { t } from "../../global-components/src/i18nTranslate";

const mosaicImages = [
  "/img/img_index/1984.webp",
  "/img/img_index/percy - mar de monstros.jpg",
  "/img/img_index/percy - maldicao do tita.jpg",
  "/img/img_index/percy - o ultimo olimpiano.jpg",
  "/img/img_index/percy - a maldicao do tita 2.jpg",
  "/img/img_index/herry - a pedra filosofal.jpg",
  "/img/img_index/harry - a ordem de fenix.jpg",
  "/img/img_index/harry - enigma do principe.jpg",
  "/img/img_index/assim que acaba.jpeg",
  "/img/img_index/percy - e os olimpianos.jpg",
  "/img/img_index/percy - a batalha do labirinto.jpg",
  "/img/img_index/biblioteca da meia noite.jpg",
  "/img/img_index/diario de anne frank.jpg",
  "/img/img_index/harry - prsioneiro de azkaban.jpg",
]

function BookMosaic() {
  const half = Math.ceil(mosaicImages.length / 2)
  const col1 = mosaicImages.slice(0, half)
  const col2 = mosaicImages.slice(half)
  return (
    <div className="auth-mosaic" aria-hidden="true">
      <div className="auth-mosaic-col auth-mosaic-col--1">
        {[...col1, ...col1].map((s, i) => <img key={i} src={s} alt="" className="auth-mosaic-img" />)}
      </div>
      <div className="auth-mosaic-col auth-mosaic-col--2">
        {[...col2, ...col2].map((s, i) => <img key={i} src={s} alt="" className="auth-mosaic-img" />)}
      </div>
    </div>
  )
}

export function Login() {
  const navigate = useNavigate();
  const { loginContext, changeUser, removeUserFromSession } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorsInput, setErrorsInput] = useState<{ login?: string[]; password?: string[], code?: string[] }>({});
  const location = useLocation();
  const [usersSessions, setUsersSessions] = useState<User[]>([]);
  const { setMessage } = useMessage();
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [userId2FA, setUserId2FA] = useState<number | null>(null);

  useEffect(() => {
    if (twoFactorCode.length === 6) handleVerify2FA();
  }, [twoFactorCode]);

  const removeUserSession = (id_user: number) => {
    removeUserFromSession(id_user);
    const users = JSON.parse(Cookies.get('saved_users') ?? "[]");
    setUsersSessions(users);
  }

  const getQueryParam = (param: string) => {
    return new URLSearchParams(location.search).get(param);
  };

  const [forceNewLogin, setForceNewLogin] = useState(getQueryParam("forceNewLogin") === "true");

  useEffect(() => {
    setForceNewLogin(getQueryParam("forceNewLogin") === "true");
  }, [location.search]);

  useEffect(() => {
    const users = JSON.parse(localStorage.getItem("saved_users") ?? "[]");
    setUsersSessions(users);
  }, []);

  useEffect(() => {
    setForceNewLogin(usersSessions.length <= 0 || getQueryParam("forceNewLogin") === "true");
  }, [usersSessions]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    setErrorsInput({});
    e.preventDefault();
    try {
      let errors: Record<string, string[]> = {};
      if (!login) errors.login = ["O campo login é obrigatório."];
      if (!password) errors.password = ["O campo senha é obrigatório."];
      if (Object.keys(errors).length > 0) throw { response: { data: { errors } } };

      const response = await axios.post(`${PACOCA_API_URL}/login`, { login, password });
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setUserId2FA(response.data.user_id);
        setLoading(false);
        return;
      }
      loginContext(response.data.access_token, response.data.user, remember);
      navigate("/");
    } catch (err: any) {
      if (err?.response?.data.errors) {
        setErrorsInput(err.response.data.errors);
      } else {
        const msg = errorMessage(err);
        setMessage(msg, "error");
      }
    }
    setLoading(false);
  };

  const handleVerify2FA = async () => {
    try {
      let errors: Record<string, string[]> = {};
      if (!twoFactorCode || twoFactorCode.length !== 6) {
        errors.code = ["O código precisa ter 6 dígitos"];
        setErrorsInput(errors);
        return;
      }
      setLoading(true);
      const response = await axios.post(`${PACOCA_API_URL}/2fa/verify`, { user_id: userId2FA, code: twoFactorCode });
      loginContext(response.data.access_token, response.data.user, remember);
      navigate("/");
    } catch (err: any) {
      if (err?.response?.data.errors) {
        setErrorsInput(err.response.data.errors);
      } else {
        const type = err?.response?.data?.type_message || "error";
        setMessage(errorMessage(err), type);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      {/* Left panel – mosaic */}
      <div className="auth-panel auth-panel--visual">
        <BookMosaic />
        <div className="auth-panel-overlay" />
        <div className="auth-panel-brand">
          <BookOpen size={28} strokeWidth={1.5} />
          <span>ReadBooks</span>
        </div>
        <p className="auth-panel-quote">
          "Uma vida inteira não é suficiente para ler todos os livros que gostaríamos."
        </p>
      </div>

      {/* Right panel – form */}
      <div className="auth-panel auth-panel--form">
        <div className="auth-form-inner">
          <div className="auth-form-header">
            <h1 className="auth-title">{t("login.connect")}</h1>
            <p className="auth-description">
              {requires2FA
                ? "Insira o código do seu aplicativo autenticador"
                : "Bem-vindo de volta à sua estante"}
            </p>
          </div>

          <form
            className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              requires2FA ? handleVerify2FA() : handleLogin(e);
            }}
          >
            {/* Saved sessions */}
            {usersSessions.length >= 1 && !forceNewLogin ? (
              <div className="auth-sessions">
                {usersSessions.map((user) =>
                  user.id ? (
                    <div className="session-card" key={user.user_id}>
                      <div className="session-card-left" onClick={() => changeUser(user.id)}>
                        <img
                          className="session-avatar"
                          src={user.img_account
                            ? `${BUCKET_PACOCA_URL}/${user.img_account.replace(/^(\.\.\/)+/, '')}`
                            : "/img/img-account.png"}
                          alt={user.name}
                          onError={(e) => { e.currentTarget.src = "/img/img-account.png"; }}
                        />
                        <div>
                          <p className="session-name">{user.name?.split(" ")[0]}</p>
                          <p className="session-username">@{user.user_name}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="session-remove"
                        onClick={() => removeUserSession(user.id)}
                        aria-label="Remover conta"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ) : null
                )}
                <button type="button" className="auth-link-btn" onClick={() => setForceNewLogin(true)}>
                  {t("login.otherAccount")}
                </button>
              </div>
            ) : (
              <>
                {requires2FA ? (
                  <div className="auth-field">
                    <label className="auth-label">Código de verificação</label>
                    <input
                      type="text"
                      placeholder="000 000"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      maxLength={6}
                      className={`auth-input auth-input--code ${errorsInput.code ? 'auth-input--error' : ''}`}
                    />
                    {errorsInput.code && <p className="auth-error">{errorsInput.code[0]}</p>}
                    <p className="auth-hint">Digite o código gerado pelo aplicativo autenticador.</p>
                  </div>
                ) : (
                  <>
                    <div className="auth-field">
                      <label className="auth-label" htmlFor="input-login">{t("login.inputLogin")}</label>
                      <input
                        id="input-login"
                        autoCapitalize="none"
                        type="text"
                        value={login}
                        onChange={(e) => setLogin(e.target.value)}
                        className={`auth-input ${errorsInput.login ? 'auth-input--error' : ''}`}
                        name="login"
                        autoComplete="username"
                        placeholder="seu@email.com ou @usuario"
                      />
                      {errorsInput.login && <p className="auth-error">{errorsInput.login[0]}</p>}
                    </div>

                    <div className="auth-field">
                      <div className="auth-label-row">
                        <label className="auth-label" htmlFor="input-password">{t("login.inputPassword")}</label>
                        <Link
                          className="auth-forgot"
                          target="_blank"
                          to={`${PACOCA_URL}/esqueci-minha-senha?login=${login}`}
                        >
                          {t("login.forgotPassword")}
                        </Link>
                      </div>
                      <div className="auth-input-wrapper">
                        <input
                          id="input-password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={`auth-input ${errorsInput.password ? 'auth-input--error' : ''}`}
                          name="password"
                          autoComplete="current-password"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          className="auth-eye"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="Mostrar senha"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errorsInput.password && <p className="auth-error">{errorsInput.password[0]}</p>}
                    </div>

                    <label className="auth-checkbox-label">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={() => setRemember(!remember)}
                        className="auth-checkbox"
                      />
                      {t("login.keepLoggedIn")}
                    </label>
                  </>
                )}

                <button type="submit" className="auth-submit" disabled={loading}>
                  {loading ? <Spinner /> : (requires2FA ? "Verificar código" : t("login.enter"))}
                </button>

                {usersSessions.length >= 1 && (
                  <button type="button" className="auth-link-btn" onClick={() => setForceNewLogin(false)}>
                    {t("login.savedAccounts")}
                  </button>
                )}

                <p className="auth-footer-text">
                  {t("login.dontHaveAccount")}{" "}
                  <Link to="/cadastro" className="auth-link">{t("login.register")}</Link>
                </p>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}